import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getArticle, getArticlesByCategory } from "../articlesData";
import { ArrowLeft } from "lucide-react";
import BookmarkButton from "../components/BookmarkButton";
import ShareButton from "../components/ShareButton";
import ArticleEngagement from "../components/ArticleEngagement";
import SEO from "../components/SEO";
import ReadingProgress from "../components/ReadingProgress";
import { shareImageFor } from "../shareCards";

const blockKeyFor = (block, i) => {
  if (typeof block === "string") return block;
  if (block.h) return `h:${block.h}`;
  if (block.img) return `img:${block.img}`;
  if (block.ul) return `ul:${block.ul.join("|")}`;
  return String(i);
};

const ArticlePage = () => {
  const { slug } = useParams();
  const article = getArticle(slug);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!article) {
    return (
      <div className="bg-[#eceae5] min-h-screen">
        <Navbar solid />
        <div className="pt-44 pb-40 text-center">
          <h1 className="font-display text-4xl text-neutral-900">Article not found</h1>
          <Link to="/" className="font-sans-ui text-neutral-600 underline mt-4 inline-block">Back home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const related = getArticlesByCategory(article.category)
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);

  return (
    <div className="bg-[#eceae5] min-h-screen">
      <ReadingProgress />
      <SEO title={article.title} description={article.excerpt} image={shareImageFor(article)} type="article" />
      <Navbar solid />

      <article className="pt-32 md:pt-40 pb-16">
        <div className="max-w-3xl mx-auto px-6">
          <Link
            to={`/topics/${article.category.toLowerCase()}`}
            className="inline-flex items-center gap-2 font-sans-ui text-sm text-neutral-500 hover:text-neutral-900 transition-colors"
          >
            <ArrowLeft size={15} /> {article.category.charAt(0) + article.category.slice(1).toLowerCase()}
          </Link>
          <h1 className="font-display text-4xl md:text-6xl text-neutral-900 mt-5 leading-[1.05]" style={{ fontWeight: 700 }}>
            {article.title}
          </h1>
          <p className="font-sans-ui text-sm text-neutral-500 mt-6">
            By {article.author} &middot; {article.date} &middot; {article.readTime}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <BookmarkButton type="article" id={article.slug} variant="inline" />
            <ShareButton title={article.title} />
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 mt-10">
          <div className="w-full aspect-[16/9] overflow-hidden rounded-md">
            <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-6 mt-12">
          {article.blocks.map((block, i) => {
            const blockKey = blockKeyFor(block, i);
            if (typeof block === "string") {
              return (
                <p key={blockKey} className="font-display text-xl text-neutral-800 leading-relaxed mb-7">
                  {block}
                </p>
              );
            }
            if (block.h) {
              return (
                <h2 key={blockKey} className="font-display text-2xl md:text-3xl text-neutral-900 mt-10 mb-4" style={{ fontWeight: 700 }}>
                  {block.h}
                </h2>
              );
            }
            if (block.ul) {
              return (
                <ul key={blockKey} className="list-disc pl-6 mb-7 flex flex-col gap-2">
                  {block.ul.map((item) => (
                    <li key={item} className="font-display text-xl text-neutral-800 leading-relaxed">
                      {item}
                    </li>
                  ))}
                </ul>
              );
            }
            if (block.img) {
              const priorImgs = article.blocks
                .slice(0, i)
                .filter((b) => b && b.img).length;
              const floatLeft = priorImgs % 2 === 1;
              return (
                <img
                  key={blockKey}
                  src={block.img}
                  alt=""
                  loading="lazy"
                  className={`rounded-md object-cover w-full mb-6 md:w-[57%] md:mt-1.5 md:mb-4 ${
                    floatLeft ? "md:float-left md:mr-7" : "md:float-right md:ml-7"
                  }`}
                />
              );
            }
            return null;
          })}
          <div className="clear-both" />
        </div>

        <ArticleEngagement slug={article.slug} />
      </article>

      {related.length > 0 && (
        <div className="max-w-[1100px] mx-auto px-6 pb-24 pt-10 border-t border-neutral-300">
          <p className="font-sans-ui uppercase tracking-[0.2em] text-sm text-neutral-500 mb-8">More on this topic</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="related-reads">
            {related.map((post) => (
              <Link key={post.slug} to={`/article/${post.slug}`} className="group" data-testid={`related-article-${post.slug}`}>
                <div className="w-full aspect-[16/9] overflow-hidden rounded-md">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                  />
                </div>
                <p className="font-sans-ui text-xs uppercase tracking-[0.18em] text-neutral-500 mt-4">
                  {post.category.charAt(0) + post.category.slice(1).toLowerCase()} &middot; {post.readTime}
                </p>
                <h3 className="font-display text-xl md:text-2xl text-neutral-900 mt-2 leading-snug group-hover:opacity-70 transition-opacity" style={{ fontWeight: 700 }}>
                  {post.title}
                </h3>
                <p className="font-display text-neutral-600 leading-relaxed mt-2 line-clamp-3">
                  {post.excerpt}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ArticlePage;
